import { useEffect, useRef, useState } from 'react';

export type UseDebounceValueOptions = {
  maxWait?: number;
};

export const resolveDebounceDelay = (
  pendingSince: number,
  now: number,
  timeout: number,
  maxWait?: number,
): number => {
  if (maxWait === undefined) {
    return timeout;
  }
  return Math.min(timeout, Math.max(0, pendingSince + maxWait - now));
};

export const useDebounceValue = <Value>(
  value: Value,
  timeout = 200,
  options?: UseDebounceValueOptions,
) => {
  const [debouncedValue, setDebouncedValue] = useState<Value>(value);
  const pendingSince = useRef<number | null>(null);
  const maxWait = options?.maxWait;

  useEffect(() => {
    if (Object.is(value, debouncedValue)) {
      pendingSince.current = null;
      return;
    }
    const now = Date.now();
    pendingSince.current = pendingSince.current ?? now;
    const delay = resolveDebounceDelay(
      pendingSince.current,
      now,
      timeout,
      maxWait,
    );
    const timer = setTimeout(() => {
      pendingSince.current = null;
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(timer);
  }, [value, timeout, maxWait, debouncedValue]);

  return debouncedValue;
};
